import { ButtonList, Skeleton } from '@dixa/lib-shared-habla';
import { AsyncStatus, Message } from 'lib-shared-legacy-types';
import { Fragment, memo, ReactNode, useEffect, useMemo, useState } from 'react';
import { useMount } from 'react-use';

import { appActions } from 'app/app.actions';
import { type ColorMode, useColorMode } from 'app/utils/colorMode';
import { HTMLShadowDomRenderer } from 'components/HTMLShadowDomRenderer';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import RecipientPill from '../../containers/messages/recipientPill';
import type { EmailMessageType } from '../../reducers/emailMessage';
import { MessageIframe } from '../messageIframe';

import TextMessage, { TextMessageProps } from './TextMessage';
import { DetailedErrorMessage } from './detailedErrorMessage';
import { useMessageFiles } from './utils/useMessageFiles';
import { useTranslatePlainTextConversationMessage } from './utils/useTranslatePlainTextConversationMessage';

type MessageEmailHTMLProps = {
  /** Conversation id */
  csid: number;
  /** The message */
  message: Message;
  /** Original email async status */
  originalEmailMeta: {
    asyncStatus: AsyncStatus;
    printerFriendly: AsyncStatus;
    htmlMessage: AsyncStatus;
  };
  /** Callback for showing the original */
  onShowOriginal?: () => void;
  /** Callback for open print dialog */
  onFetchPrinter: () => void;
  /** Fetch orignal email msg */
  originalEmailGetHtmlMessage: (payload: {
    url: string;
    csid: number;
  }) => Promise<any>;
  /** Original email URL */
  originalUrl: string;
  /** The html messages. */
  htmlMessages: Array<EmailMessageType>;
} & TextMessageProps;

const translatedTextWrapperId = 'translated-text-wrapper';

// We have wrapped the translated text in a div and we need to respect \n
// in the content. If the wrapped text is html itself, \n should not be
// respected, hence the child tags (in case the content is html) have
// initial white space.
const fixTranslationPlainTextNewLinesStyle = `
  #${translatedTextWrapperId} { white-space: pre-wrap; }
  #${translatedTextWrapperId} * { white-space: initial; }
  `;

const MessageEmailHTML = (props: MessageEmailHTMLProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const flags = useFeatureFlag([
    'shadow-dom-messages',
    'dark-mode',
    'translate-html',
  ]);
  const [content, setContent] = useState<string | null>(null);
  const [contentIsPlainText, setContentIsPlainText] = useState<boolean>(false);
  const [renderedContent, setRenderedContent] = useState(content);

  const [loading, setLoading] = useState(false);

  const { isColorModeSupported, colorMode } = useColorMode();
  const [messageColorMode, setMessageColorMode] = useState<ColorMode>();

  const translationText = flags.translateHtml
    ? (renderedContent ?? props.message.text ?? undefined)
    : (props.message.text ?? undefined);

  const contentType: 'html' | 'text' = useMemo(() => {
    if (flags.translateHtml) {
      return renderedContent || props.message.content_type === 'Html'
        ? 'html'
        : 'text';
    }
    return props.message.content_type === 'Html' ? 'html' : 'text';
  }, [renderedContent, flags.translateHtml, props.message.content_type]);

  const { button, translation } = useTranslatePlainTextConversationMessage({
    text: translationText,
    originalLanguageCode: props.message.language ?? null,
    contentType,
  });

  useMount(() => {
    const urlArray = props.originalUrl.split('/');
    const messageId = urlArray.pop();

    const foundMessage = props.htmlMessages.find(
      (m: EmailMessageType) => m.messageId === messageId,
    );
    if (foundMessage) {
      setContent(foundMessage.rewritten_html);
    } else {
      setLoading(true);
      props
        .originalEmailGetHtmlMessage({
          url: props.originalUrl,
          csid: props.csid,
        })
        .then((r: any) => {
          setContent(r.msg?.rewritten_html || r.rewritten_html || null);
          // This will be used when we will render text stored in S3.
          setContentIsPlainText(!r.msg?.rewritten_html && !r.rewritten_html);
        })
        .catch((e: any) => {
          console.error(e);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  });

  useEffect(() => setRenderedContent(content), [content]);

  const prepareRecipientMenu = ({
    cc_list_v2,
    bcc_list_v2,
    to_list_v2,
    to,
    from,
  }: Message): JSX.Element | null => {
    const toArray = to ? [to] : [];
    const toList = Array.isArray(to_list_v2) ? to_list_v2 : toArray;
    const ccList = Array.isArray(cc_list_v2) ? cc_list_v2 : [];
    const bccList = Array.isArray(bcc_list_v2) ? bcc_list_v2 : [];

    if (toList.length || ccList.length || bccList.length) {
      const fromAttributeAsRecipient = {
        email: from ? from : null,
        name: null,
      };
      return (
        <RecipientPill
          id={`cc-bcc-pill-${props.message.id}`}
          from={fromAttributeAsRecipient}
          toList={toList}
          ccList={ccList}
          bccList={bccList}
        />
      );
    }
    return null;
  };

  const {
    message,
    onShowOriginal,
    onFetchPrinter,
    originalEmailGetHtmlMessage,
    originalEmailMeta,
    originalUrl,
    htmlMessages,
    ...otherProps
  } = props;

  const menu: ReactNode[] = [
    ...(typeof otherProps.failed === 'string'
      ? [
          <Fragment key="fail">
            <DetailedErrorMessage
              message={otherProps.failed}
              description={otherProps.failureReason}
            />
          </Fragment>,
        ]
      : []),
    // Only show email headers option if we don't have "view original" option because that contains the headers too
    ...(!onShowOriginal && message.email_headers
      ? [
          <>
            <ButtonList.Item
              icon="code"
              onClick={() => {
                dispatch(
                  appActions.showModal({
                    modal: {
                      type: 'conversation:emailHeaders',
                      arg: {
                        emailHeaders: message.email_headers as string,
                      },
                    },
                  }),
                );
              }}
            >
              View email headers
            </ButtonList.Item>
          </>,
        ]
      : []),
    ...(onShowOriginal
      ? [
          <Fragment key="orginal">
            <ButtonList.Item
              icon="mail"
              isLoading={originalEmailMeta.asyncStatus === 'loading'}
              onClick={onShowOriginal}
            >
              View original email
            </ButtonList.Item>
            <ButtonList.Item
              icon="print"
              isLoading={originalEmailMeta.printerFriendly === 'loading'}
              onClick={onFetchPrinter}
            >
              Print email
            </ButtonList.Item>
          </Fragment>,
        ]
      : []),
    ...(isColorModeSupported && colorMode === 'dark'
      ? [
          <Fragment key="colorMode">
            <ButtonList.Divider />

            <ButtonList.Item
              icon={messageColorMode === 'light' ? 'moon' : 'sun'}
              onClick={() => {
                setMessageColorMode(
                  messageColorMode === 'light' ? 'dark' : 'light',
                );
              }}
            >
              View in {messageColorMode === 'light' ? 'dark' : 'light'} mode
            </ButtonList.Item>
          </Fragment>,
        ]
      : []),
  ];

  const messageContent = useMemo(() => {
    if (!renderedContent) {
      return;
    }

    const messageProps = {
      content: renderedContent,
      isPlainText: contentIsPlainText,
    };

    if (flags.shadowDomMessages) {
      return (
        <HTMLShadowDomRenderer
          html={renderedContent}
          stylesheets={
            translation ? [fixTranslationPlainTextNewLinesStyle] : undefined
          }
          colorMode={messageColorMode ?? colorMode}
        />
      );
    }
    return <MessageIframe {...messageProps} />;
  }, [
    contentIsPlainText,
    colorMode,
    flags.shadowDomMessages,
    messageColorMode,
    renderedContent,
    translation,
  ]);

  const { attachments, images } = useMessageFiles(
    message.inline_images,
    message.attached_files,
  );

  useEffect(() => {
    if (
      translation?.status === 'uninitialized' ||
      translation?.status === 'rejected'
    ) {
      setRenderedContent(content);
      return;
    }

    if (translation?.status === 'fulfilled' && translation) {
      setRenderedContent(
        `<div id="${translatedTextWrapperId}">${translation.translatedText}</div>`,
      );
      return;
    }
  }, [content, translation]);
  if (loading) {
    return (
      <Skeleton
        width="100%"
        height="100%"
        minHeight="80px"
        margin="8px"
        animation="pulse"
      />
    );
  }

  return (
    <div
      style={{
        transition: 'height 0.3s ease-in-out',
      }}
      {...(messageColorMode && {
        'data-color-mode': messageColorMode,
      })}
    >
      <TextMessage
        {...otherProps}
        attachments={attachments}
        images={images}
        nameMenu={prepareRecipientMenu(message)}
        menu={menu}
        content={messageContent}
        message={message}
        gutterButtons={button}
        translation={translation}
      />
    </div>
  );
};

export default memo(MessageEmailHTML);
